import moment from 'moment';

import { list } from '../../../../../../common/classes/request-builder';
import { StorageManager } from '../../../../../../common/storage-manager.class';
import { environment } from '../../../../../../../environments/environment';
import {
    DropdownData,
    FilteredValue,
    InputType,
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
} from '@fitogest/reactive-form-builder/interfaces';
import { TareaSiembraModel } from '../../../../../../models/tareas/tarea-siembra.model';
import { ClienteModel } from '../../../../../../models/cliente.model';
import { FincasModel } from '../../../../../../models/form-common/fincas.module';
import { SectorModel } from '../../../../../../models/form-common/sector.model';
import { ParcelasModel } from '../../../../../../models/form-common/parcelas.model';
import { MantenimientoModel } from '../../../../../../models/trabajadores/mantenimiento.model';
import { VisitasVariedadMesModel } from '../../../../../../models/visitas_variedad_mes/visitas_variedad_mes.model';
import { ProductosModel } from '../../../../../../models/productos/productos.model';
import { TratamientosModel } from '../../../../../../models/tratamientos/tratamientos.model';
import { MaquinariaModel } from '../../../../../../models/trabajadores/maquinaria.model';
import { getDropdownSelected, getDropdownSelectedSingle } from '@fitogest/reactive-form-builder/utils';

export class SiembraFormDefinition {
    public readonly appName = environment.appName;
    public readonly applicationType = environment.features.applicationType;
    public readonly horticola = environment.features.horticola;
    public readonly isViverogest = this.applicationType === 'viverogest' && !this.horticola;
    public readonly fincaName = environment.features.fincaName;
    public readonly sectorName = environment.features.sectorName;
    public readonly parcelasName = environment.features.parcelasName;
    public readonly clienteName = environment.features.clienteName;
    public readonly textoSuperficie = environment.features.textoSuperficie;
    public readonly tipoSuperficieParcelas = environment.features.hasSuperficieCultivada ? 'S. C.' : 'S. Sigpac';
    public readonly hasClients = environment.features.hasClients;
    public readonly canSeeClientes = environment.features.hasClients &&
        environment.features.userCanSeeAllRecords &&
        environment.features.userCanSeeClientes
        ? true
        : StorageManager.getUser().rol === 'admin';
    public readonly showTareasSiembraProductos = environment.features.showTareasSiembraProductos;
    public readonly showTareasSiembraMaquinarias = environment.features.showTareasSiembraMaquinarias;

    public formType!: FormType;
    public model: Partial<TareaSiembraModel> = {};
    public formFields!: Form<TareaSiembraModel>;

    public clientes: DropdownData<ClienteModel> = list<ClienteModel>(['...', null]);
    public fincas: DropdownData<FincasModel> = list<FincasModel>(['...', null]);
    public sectores: DropdownData<SectorModel> = list<SectorModel>(['...', null]);
    public parcelas: DropdownData<ParcelasModel> = list<ParcelasModel>(['...', null]);
    public trabajadores: DropdownData<MantenimientoModel> = list<MantenimientoModel>(['...', null]);
    public cultivos: DropdownData<VisitasVariedadMesModel> = list<VisitasVariedadMesModel>(['...', null]);
    public variedades: DropdownData<VisitasVariedadMesModel> = list<VisitasVariedadMesModel>(['...', null]);
    public cultivosPrecedentes: DropdownData<VisitasVariedadMesModel> = list<VisitasVariedadMesModel>(['...', null]);
    public productos: DropdownData<ProductosModel> = list<ProductosModel>(['...', null]);
    public tratamientos: DropdownData<TratamientosModel> = list<TratamientosModel>(['...', null]);
    public maquinarias: DropdownData<MaquinariaModel> = list<MaquinariaModel>(['...', null]);

    public tipoRiego: DropdownData<string> = list<string>('...', 'Cintas', 'Gotero', 'Aspersión');
    public productoArmentia: DropdownData<string> = list<string>('...', 'Systiva', 'Raxil Plus');
    public tipoSemilla: DropdownData<string> = list<string>(['...', null], 'R1', 'R2', 'S');
    public tipoSiembra: DropdownData<string> = list<string>('...', 'Seco', 'Agua');
    public tipoLabor: DropdownData<string> = this.appName !== 'cartagenafresh'
        ? list<string>(['...', null], 'CHISEL', 'GRADA', 'LABRAR', 'SEMBRAR', 'RECOLECTAR')
        : list<string>(['SEMBRAR', 'SEMBRAR']);
    public semilla: DropdownData<string> = list<string>(['...', null], ['R - Reempleo', 'R'], ['C - Certificada', 'C'], ['N - Nada', 'N']);
    public sustrato: DropdownData<string> = list<string>('...', 'BVB', 'BRILL', 'COCO');
    public camara: DropdownData<string> = list<string>('...', 'Calor', 'Frio', '4º', '30º', 'Descarga Directa (no pasa por cámara)');
    public rpmValues: DropdownData<string> = list<string>(['...', null], '1000', '1100', '1200', '1300', '1400',
        '1500', '1600', '1700', '1800', '1900', '2000');

    public fieldNames: FormField<TareaSiembraModel>[] = [];
    public fieldLabels: string[] = [];

    private readonly client = StorageManager.getClient();
    private readonly user = StorageManager.getUser();

    constructor(type: FormType, model: Partial<TareaSiembraModel>) {
        this.formType = +type;
        this.model = model;
        this.ensureModelDefaults();

        this.formFields = {
            name: 'tareas-siembra',
            header: this.getHeader(),
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
        };
    }

    public calculateHorasTotales(): string {
        return this.formatHoursMinutes(
            this.normalizeTimeValue(this.model.hora_inicio),
            this.normalizeTimeValue(this.model.hora_fin),
        );
    }

    public updateCultivoVariedadVisibility(): void {
        const variedadField = this.getFieldByName('id_variedad');

        if (!variedadField) {
            return;
        }

        variedadField.input.dropdownConfiguration = {
            ...(variedadField.input.dropdownConfiguration ?? {}),
            valuePrimaryKey: 'id',
            filter: (it): boolean => this.filterVariedad(it),
        };
    }

    private getHeader(): string {
        if (this.formType === FormType.EDITION) {
            return 'Editar ' + (this.horticola ? 'Cultivo' : 'Tarea Siembra');
        }

        if (this.formType === FormType.DUPLICATION) {
            return 'Duplicar ' + (this.horticola ? 'Cultivo' : 'Tarea Siembra');
        }

        return 'Crear ' + (this.horticola ? 'Cultivo' : 'Tarea Siembra');
    }

    private getFormSections(): FormSection<TareaSiembraModel>[] {
        const sections: FormSection<TareaSiembraModel>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.horticola ? this.getHorticolaFields() : this.isViverogest ? this.getViverogestFields() : this.getBaseFields(),
                order: 1,
            },
            {
                name: 'Datos de la maquinaria',
                width: 6,
                isVisible: this.showTareasSiembraMaquinarias && !this.horticola,
                fields: this.getMaquinariaFields(this.showTareasSiembraMaquinarias && !this.horticola),
                order: 2,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 3,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getBaseFields(): FormField<TareaSiembraModel>[] {
        return this.pushFields([
            this.getClientField(1),
            {
                name: 'check_previa',
                vinculatedFields: undefined,
                label: 'NO < Tarea previa a la siembra > SÍ',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: 'fecha_inicio',
                vinculatedFields: undefined,
                label: 'Fecha de inicio',
                input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'cartagenafresh' }),
                order: 3,
            },
            {
                name: 'fecha_fin',
                vinculatedFields: undefined,
                label: this.appName === 'cartagenafresh' ? 'Fecha Plantación' : 'Fecha de fin',
                input: {
                    type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.CALENDAR,
                    isEditable: true,
                    ...(this.getDefaultFechaFin() !== undefined ? { defaultValue: this.getDefaultFechaFin() } : {}),
                },
                width: 3,
                conditions: this.conditions(),
                order: 4,
            },
            {
                name: 'hora_inicio',
                vinculatedFields: undefined,
                label: 'Hora Inicio',
                input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.TIME, isEditable: true },
                width: 3,
                conditions: this.conditions({
                    isRequired: environment.appName === 'agrotata',
                    isVisible: environment.features.timeStartStopTareas && this.appName !== 'hernandorena',
                }),
                order: 5,
            },
            {
                name: 'hora_fin',
                vinculatedFields: undefined,
                label: 'Hora Fin',
                input: {
                    type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.TIME,
                    isEditable: true,
                    ...(this.getDefaultHoraFin() !== undefined ? { defaultValue: this.getDefaultHoraFin() } : {}),
                },
                width: 3,
                conditions: this.conditions({
                    isRequired: environment.appName === 'agrotata',
                    isVisible: environment.features.timeStartStopTareas && this.appName !== 'hernandorena',
                }),
                order: 6,
            },
            {
                name: 'horas_totales',
                vinculatedFields: ['hora_inicio', 'hora_fin'],
                label: 'Horas',
                input: { type: InputType.ONLY_TEXT, isEditable: true },
                width: 3,
                computed: {
                    dependsOn: ['hora_inicio', 'hora_fin'],
                    resolve: (context) => this.formatHoursMinutes(
                        this.normalizeTimeValue(context.getValue('hora_inicio')),
                        this.normalizeTimeValue(context.getValue('hora_fin')),
                    ),
                },
                updateValue: () => this.calculateHorasTotales(),
                conditions: this.conditions({ isVisible: environment.features.showTareasMto || environment.features.tareasHorasTotales }),
                order: 7,
            },
            {
                name: 'facturado',
                vinculatedFields: undefined,
                label: 'Facturado',
                input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'agrosalvi' }),
                order: 8,
            },
            {
                name: 'factura_albaran',
                vinculatedFields: undefined,
                label: 'Factura/Albarán',
                input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'agrosalvi' }),
                order: 9,
            },
            this.getFincaField(10),
            this.getSectorField(11, true),
            this.getParcelasField(12, true),
            {
                name: this.isReadonlyMode() ? 'cultivo_precedente' : 'id_cultivo_precedente',
                dependencies: [
                    {
                        source: 'id_finca',
                        target: this.isReadonlyMode() ? 'cultivo_precedente' : 'id_cultivo_precedente',
                        kind: 'filter'
                    },
                    {
                        source: 'id_sector',
                        target: this.isReadonlyMode() ? 'cultivo_precedente' : 'id_cultivo_precedente',
                        kind: 'filter'
                    },
                    {
                        source: 'ids_parcelas',
                        target: this.isReadonlyMode() ? 'cultivo_precedente' : 'id_cultivo_precedente',
                        kind: 'filter'
                    }
                ],
                vinculatedFields: undefined,
                label: 'Cultivo precedente',
                input: this.isReadonlyMode()
                    ? { type: InputType.ONLY_TEXT, isEditable: true }
                    : {
                        type: InputType.DROPDOWN_SEARCH,
                        data: this.cultivosPrecedentes,
                        isEditable: true,
                        dropdownConfiguration: {
                            valuePrimaryKey: 'id',
                            filter: (it): boolean => this.filterDistinctCultivo(it),
                        },
                    },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'cartagenafresh' }),
                order: 13,
            },
            {
                name: 'tipo_tratamiento',
                vinculatedFields: undefined,
                label: 'Tipo de tratamiento',
                input: {
                    type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.DROPDOWN,
                    data: list<string>(['...', null], 'Semillas', 'Plantones', 'Cosecha'),
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'pyf' }),
                order: 14,
            },
            {
                name: 'tipo_riego',
                vinculatedFields: undefined,
                label: 'Tipo riego',
                input: {
                    type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.DROPDOWN,
                    data: this.tipoRiego,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'cartagenafresh' }),
                order: 15,
            },
            {
                name: 'motivo',
                vinculatedFields: undefined,
                label: 'Motivo',
                input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'pyf' }),
                order: 16,
            },
            this.getProductoField(17),
            {
                name: 'dosis_siembra',
                vinculatedFields: undefined,
                label: this.applicationType === 'cropgest' ? 'Dosis Kg' : 'Dosis Kg/Ha',
                input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 18,
            },
            {
                name: 'tipo_labor',
                vinculatedFields: undefined,
                label: 'Tipo de labor',
                input: {
                    type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.DROPDOWN,
                    data: this.tipoLabor,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                order: 19,
            },
            {
                name: 'semilla',
                vinculatedFields: undefined,
                label: 'Semilla',
                input: {
                    type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.DROPDOWN,
                    data: this.semilla,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                order: 20,
            },
            {
                name: 'tipo_semilla',
                vinculatedFields: undefined,
                label: (this.appName === 'sequiador' || this.appName === 'SIEX') ? 'Tipo de siembra' : 'Tipo de semilla',
                input: {
                    type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.DROPDOWN,
                    data: (this.appName === 'sequiador' || this.appName === 'SIEX') ? this.tipoSiembra : this.tipoSemilla,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'cartagenafresh' }),
                order: 21,
            },
            {
                name: 'lote_semilla',
                vinculatedFields: undefined,
                label: 'Lote Semilla',
                input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'cartagenafresh' }),
                order: 22,
            },
            this.getObservacionesField(23, 6),
        ]);
    }

    private getViverogestFields(): FormField<TareaSiembraModel>[] {
        return this.pushFields([
            {
                name: this.isReadonlyMode() ? 'trabajadores_nombre' : 'ids_trabajadores',
                vinculatedFields: undefined,
                label: 'Personal Siembra',
                input: this.isReadonlyMode()
                    ? { type: InputType.ONLY_TEXT, isEditable: true }
                    : {
                        type: InputType.MULTISELECT,
                        data: this.trabajadores,
                        isEditable: true,
                        dropdownConfiguration: {
                            valuePrimaryKey: 'id',
                            filter: (it): boolean => (it.value as MantenimientoModel | null)?.activo?.toString() === '1',
                        },
                    },
                width: 4,
                conditions: this.conditions(),
                order: 1,
            },
            this.getFincaField(2),
            this.getSectorField(3, true),
            this.getParcelasField(4, true),
            { name: 'cantidad_semilla', vinculatedFields: undefined, label: 'Cantidad Semilla', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions(), order: 5 },
            { name: 'fecha_siembra', vinculatedFields: undefined, label: 'Fecha Siembra', input: { type: this.dateInputType(), isEditable: true }, width: 4, conditions: this.conditions(), order: 6 },
            { name: 'sustrato', vinculatedFields: undefined, label: 'Sustrato', input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.DROPDOWN, data: this.sustrato, isEditable: true }, width: 4, conditions: this.conditions(), order: 7 },
            { name: 'color', vinculatedFields: undefined, label: 'Color', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions(), order: 8 },
            { name: 'formato', vinculatedFields: undefined, label: 'Formato', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions(), order: 9 },
            { name: 'semana_siembra', vinculatedFields: undefined, label: 'Semana Siembra', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions(), order: 10 },
            { name: 'tamizado', vinculatedFields: undefined, label: 'Tamizado', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions(), order: 11 },
            { name: 'lote', vinculatedFields: undefined, label: 'Lote', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions(), order: 12 },
            { name: 'cantidad_bandejas', vinculatedFields: undefined, label: 'Cantidad Bandejas', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions(), order: 13 },
            { name: 'camara', vinculatedFields: undefined, label: 'Cámara', input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.DROPDOWN, data: this.camara, isEditable: true }, width: 4, conditions: this.conditions(), order: 14 },
            { name: 'tratamiento', vinculatedFields: undefined, label: 'Tratamiento', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions(), order: 15 },
            { name: 'completado', vinculatedFields: undefined, label: 'Completado', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions(), order: 16 },
            { name: 'fecha_descarga', vinculatedFields: undefined, label: 'Fecha Descarga', input: { type: this.dateInputType(), isEditable: true }, width: 4, conditions: this.conditions(), order: 17 },
            this.getObservacionesField(18, 4),
            { name: 'punta', vinculatedFields: undefined, label: 'Punta', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions(), order: 19 },
            { name: 'semana_entrega', vinculatedFields: undefined, label: 'Semana Entrega', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions(), order: 20 },
            { name: 'porcentaje_germinacion', vinculatedFields: undefined, label: '% Germinación', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions(), order: 21 },
            { name: 'remesa', vinculatedFields: undefined, label: 'Remesa', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions(), order: 22 },
            { name: 'no_esta', vinculatedFields: undefined, label: 'No Está', input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.SWITCH, isEditable: true }, width: 4, conditions: this.conditions(), order: 23 },
        ]);
    }

    private getHorticolaFields(): FormField<TareaSiembraModel>[] {
        return this.pushFields([
            {
                name: 'fecha_inicio',
                vinculatedFields: undefined,
                label: 'Fecha de inicio',
                input: { type: this.dateInputType(), isEditable: true },
                width: 4,
                conditions: this.conditions({ isVisible: this.appName !== 'cartagenafresh' }),
                order: 1,
            },
            {
                name: 'fecha_fin',
                vinculatedFields: undefined,
                label: this.appName === 'cartagenafresh' ? 'Fecha Plantación' : 'Fecha de fin',
                input: {
                    type: this.dateInputType(),
                    isEditable: true,
                    ...(this.getDefaultFechaFin() !== undefined ? { defaultValue: this.getDefaultFechaFin() } : {}),
                },
                width: 4,
                conditions: this.conditions(),
                order: 2,
            },
            this.getFincaField(3),
            this.getSectorField(4, true),
            this.getParcelasField(5, false),
            {
                name: this.isReadonlyMode() ? 'cultivo' : 'id_cultivo',
                vinculatedFields: undefined,
                label: 'Cultivo',
                input: this.isReadonlyMode()
                    ? { type: InputType.ONLY_TEXT, isEditable: true }
                    : {
                        type: InputType.DROPDOWN_SEARCH,
                        data: this.cultivos,
                        isEditable: true,
                        dropdownConfiguration: { valuePrimaryKey: 'id' },
                    },
                width: 4,
                conditions: this.conditions(),
                order: 6,
            },
            {
                name: this.isReadonlyMode() ? 'variedad' : 'id_variedad',
                dependencies: [
                    {
                        source: 'id_cultivo',
                        target: this.isReadonlyMode() ? 'variedad' : 'id_variedad',
                        kind: 'filter'
                    }
                ],
                vinculatedFields: undefined,
                label: 'Variedad',
                input: this.isReadonlyMode()
                    ? { type: InputType.ONLY_TEXT, isEditable: true }
                    : {
                        type: InputType.DROPDOWN_SEARCH,
                        data: this.variedades,
                        isEditable: true,
                        dropdownConfiguration: {
                            valuePrimaryKey: 'id',
                            filter: (it): boolean => this.filterVariedad(it),
                        },
                    },
                width: 4,
                conditions: this.conditions(),
                order: 7,
            },
        ]);
    }

    private getMaquinariaFields(isSectionVisible: boolean): FormField<TareaSiembraModel>[] {
        return this.pushFields([
            {
                name: this.appName !== 'bravosol' ? 'id_maquinaria' : 'ids_maquinaria',
                vinculatedFields: undefined,
                label: 'Maquinaria',
                input: {
                    type: this.isReadonlyMode()
                        ? InputType.ONLY_TEXT
                        : this.appName !== 'bravosol' ? InputType.DROPDOWN_SEARCH : InputType.MULTISELECT,
                    data: this.maquinarias,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it): boolean => (it.value as MaquinariaModel | null)?.activo?.toString() === '1',
                    },
                },
                width: 4,
                conditions: this.conditions({ isVisible: isSectionVisible && this.appName !== 'bravosol' }),
                order: 1,
            },
            { name: 'velocidad', vinculatedFields: undefined, label: 'Velocidad (Km/h)', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions({ isVisible: isSectionVisible }), order: 2 },
            { name: 'velocidad_esponja', vinculatedFields: undefined, label: 'Velocidad Esponja', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions({ isVisible: isSectionVisible }), order: 3 },
            { name: 'marcha', vinculatedFields: undefined, label: 'Marcha', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions({ isVisible: isSectionVisible }), order: 4 },
            { name: 'rpm', vinculatedFields: undefined, label: 'RPM', input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.DROPDOWN, data: this.rpmValues, isEditable: true }, width: 4, conditions: this.conditions({ isVisible: isSectionVisible }), order: 5 },
            { name: 'presion', vinculatedFields: undefined, label: 'Presión (atm-bar)', input: { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.NUMERIC, isEditable: true }, width: 4, conditions: this.conditions({ isVisible: isSectionVisible }), order: 6 },
            { name: 'boquillas', vinculatedFields: undefined, label: 'Boquillas', input: { type: this.textInputType(), isEditable: true }, width: 4, conditions: this.conditions({ isVisible: isSectionVisible }), order: 7 },
        ]);
    }

    private getHiddenFields(): FormField<TareaSiembraModel>[] {
        const hiddenFields: FormField<TareaSiembraModel>[] = [
            { name: 'id', vinculatedFields: undefined, label: '', input: { type: InputType.HIDDEN, isEditable: false, defaultValue: this.model.id ?? '' }, width: 1, conditions: this.conditions(), order: 1 },
            {
                name: 'id_usuario',
                vinculatedFields: undefined,
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false, defaultValue: this.user.id ?? '' },
                width: 1,
                updateValue: () => String(this.model.id_usuario ?? this.user.id ?? ''),
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: 'cultivo_precedente',
                vinculatedFields: ['id_cultivo_precedente'],
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 1,
                updateValue: () => getDropdownSelectedSingle(this.cultivosPrecedentes)?.cultivo ?? this.model.cultivo_precedente ?? '',
                conditions: this.conditions({ isVisible: !this.horticola && !this.isReadonlyMode() }),
                order: 3,
            },
            {
                name: 'cultivo',
                vinculatedFields: ['ids_parcelas', 'id_cultivo'],
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 1,
                updateValue: () => this.resolveCultivoValue(),
                conditions: this.conditions({ isVisible: !this.isReadonlyMode() }),
                order: 4,
            },
            {
                name: 'variedad',
                vinculatedFields: ['ids_parcelas', 'id_variedad'],
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 1,
                updateValue: () => this.resolveVariedadValue(),
                conditions: this.conditions({ isVisible: !this.isReadonlyMode() }),
                order: 5,
            },
            {
                name: 'sup_cultivada',
                vinculatedFields: ['ids_parcelas'],
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 1,
                updateValue: () => this.getSelectedParcelas().map((it) => it.superficie_cultivada).join(';'),
                conditions: this.conditions({ isVisible: !this.horticola && !this.isReadonlyMode() }),
                order: 6,
            },
        ];

        if (!(this.canSeeClientes && !this.client && this.hasClients) && !this.isReadonlyMode()) {
            hiddenFields.push({
                name: 'id_cliente',
                vinculatedFields: ['id_finca'],
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false, defaultValue: this.resolveClientId() },
                width: 1,
                updateValue: () => this.resolveClientId(),
                conditions: this.conditions(),
                order: 7,
            });
        }

        return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getClientField(order: number): FormField<TareaSiembraModel> {
        const visible = this.canSeeClientes && !this.client && this.hasClients;

        return {
            name: visible || this.isReadonlyMode() ? (this.isReadonlyMode() ? 'cliente_nombre' : 'id_cliente') : 'id_cliente_hidden_visual',
            vinculatedFields: undefined,
            label: this.clienteName,
            input: visible
                ? {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.clientes,
                    isEditable: true,
                    dropdownConfiguration: { valuePrimaryKey: 'id' },
                }
                : { type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.HIDDEN, isEditable: false },
            width: 3,
            conditions: this.conditions({ isRequired: visible, isVisible: visible || this.isReadonlyMode() }),
            order,
        };
    }

    private getFincaField(order: number): FormField<TareaSiembraModel> {
        const readonly = this.isReadonlyMode();

        return {
            name: readonly ? 'finca_nombre' : 'id_finca',
            dependencies: [
                {
                    source: 'id_cliente',
                    target: readonly ? 'finca_nombre' : 'id_finca',
                    kind: 'filter'
                }
            ],
            vinculatedFields: undefined,
            label: this.fincaName,
            input: readonly
                ? { type: InputType.ONLY_TEXT, isEditable: true }
                : {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.fincas,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it): boolean => this.filterFinca(it),
                    },
                },
            width: 3,
            conditions: this.conditions(),
            order,
        };
    }

    private getSectorField(order: number, multi: boolean): FormField<TareaSiembraModel> {
        const readonly = this.isReadonlyMode();

        return {
            name: readonly ? 'sector_nombre' : 'id_sector',
            dependencies: [
                {
                    source: 'id_finca',
                    target: readonly ? 'sector_nombre' : 'id_sector',
                    kind: 'filter'
                }
            ],
            vinculatedFields: undefined,
            label: this.sectorName,
            input: readonly
                ? { type: InputType.ONLY_TEXT, isEditable: true }
                : {
                    type: multi ? InputType.MULTISELECT_SEARCH : InputType.DROPDOWN_SEARCH,
                    data: this.sectores,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it): boolean => this.filterSector(it),
                    },
                },
            width: 3,
            conditions: this.conditions(),
            order,
        };
    }

    private getParcelasField(order: number, multi: boolean): FormField<TareaSiembraModel> {
        const readonly = this.isReadonlyMode();

        return {
            name: readonly ? 'parcelas_nombres' : 'ids_parcelas',
            dependencies: [
                {
                    source: 'id_finca',
                    target: readonly ? 'parcelas_nombres' : 'ids_parcelas',
                    kind: 'filter'
                },
                {
                    source: 'id_sector',
                    target: readonly ? 'parcelas_nombres' : 'ids_parcelas',
                    kind: 'filter'
                }
            ],
            vinculatedFields: undefined,
            label: this.parcelasName,
            input: readonly
                ? { type: InputType.ONLY_TEXT, isEditable: true }
                : {
                    type: multi ? InputType.MULTISELECT_SEARCH : InputType.DROPDOWN_SEARCH,
                    data: this.parcelas,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        ...(multi ? {
                            summation: {
                                summand: 'superficie_cultivada',
                                name: this.tipoSuperficieParcelas,
                                uds: this.textoSuperficie,
                                format: (total: number): string => total.toLocaleString('es-ES', {
                                    useGrouping: true,
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                }),
                            },
                        } : {}),
                        filter: (it): boolean => this.filterParcela(it),
                    },
                },
            width: 3,
            conditions: this.conditions({ isRequired: !readonly }),
            order,
        };
    }

    private getProductoField(order: number): FormField<TareaSiembraModel> {
        const readonly = this.isReadonlyMode();
        const useArmentiaList = this.appName === 'armentia';

        if (readonly) {
            return {
                name: this.showTareasSiembraProductos ? 'producto_nombre' : 'id_producto',
                vinculatedFields: undefined,
                label: 'Producto/Tratamiento',
                input: { type: InputType.ONLY_TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.showTareasSiembraProductos || this.appName !== 'cartagenafresh' }),
                order,
            };
        }

        return {
            name: 'id_producto',
            vinculatedFields: undefined,
            label: 'Producto/Tratamiento',
            input: {
                type: InputType.DROPDOWN_SEARCH,
                data: useArmentiaList ? this.productoArmentia : this.productos,
                isEditable: true,
                ...(useArmentiaList ? {} : {
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it): boolean => (it.value as ProductosModel | null)?.tipo === 'fitosanitario',
                    },
                }),
            },
            width: 3,
            conditions: this.conditions({ isVisible: this.showTareasSiembraProductos }),
            order,
        };
    }

    private getObservacionesField(order: number, width: number): FormField<TareaSiembraModel> {
        return {
            name: 'observaciones',
            vinculatedFields: undefined,
            label: 'Observaciones',
            input: {
                type: this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.TEXTAREA,
                isEditable: true,
                textAreaConfiguration: {
                    hasVoiceTranscription: true,
                    canTakePhoto: false,
                    photoField: undefined,
                },
            },
            width,
            conditions: this.conditions(),
            order,
        };
    }

    private filterFinca(it: FilteredValue<unknown>): boolean {
        const finca = it.value as FincasModel | null;

        if (!finca) {
            return true;
        }

        if (!this.canSeeClientes || this.formType === FormType.DUPLICATION) {
            return true;
        }

        const selectedCliente = getDropdownSelectedSingle(this.clientes);

        if (selectedCliente) {
            return finca.id_cliente?.toString() === selectedCliente.id?.toString();
        }

        if (this.client) {
            return finca.id_cliente?.toString() === this.client.id?.toString();
        }

        return true;
    }

    private filterSector(it: FilteredValue<unknown>): boolean {
        const sector = it.value as SectorModel | null;

        if (!sector) {
            return true;
        }

        return sector.id_finca === getDropdownSelectedSingle(this.fincas)?.id;
    }

    private filterParcela(it: FilteredValue<unknown>): boolean {
        const parcela = it.value as ParcelasModel | null;

        if (!parcela || parcela.activo?.toString() !== '1') {
            return false;
        }

        const selectedSectors = getDropdownSelected(this.sectores);

        if (Array.isArray(selectedSectors) && selectedSectors.length > 0) {
            return selectedSectors.some((sector) => sector.id?.toString() === parcela.id_sector?.toString());
        }

        if (selectedSectors && typeof selectedSectors === 'object' && !Array.isArray(selectedSectors)) {
            return selectedSectors.id?.toString() === parcela.id_sector?.toString();
        }

        const selectedFinca = getDropdownSelectedSingle(this.fincas);

        return selectedFinca ? parcela.id_finca === selectedFinca.id : false;
    }

    private filterDistinctCultivo(it: FilteredValue<unknown>): boolean {
        const cultivo = it.value as VisitasVariedadMesModel | null;

        if (!cultivo) {
            return true;
        }

        const firstSameLabel = this.cultivosPrecedentes.values.find((value) => value.label === it.label);

        return firstSameLabel?.value?.id === cultivo.id;
    }

    private filterVariedad(it: FilteredValue<unknown>): boolean {
        const variedad = it.value as VisitasVariedadMesModel | null;

        if (!variedad) {
            return true;
        }

        const selectedCultivo = getDropdownSelectedSingle(this.cultivos);

        if (selectedCultivo) {
            return variedad.codigo_cultivo === selectedCultivo.codigo;
        }

        const modelCultivo = this.model.id_cultivo;

        if (!modelCultivo) {
            return true;
        }

        const found = this.cultivos.filtered.find((cultivo) => cultivo.value?.id?.toString() === modelCultivo?.toString());

        return found?.value ? variedad.codigo_cultivo === found.value.codigo : true;
    }

    private resolveCultivoValue(): string {
        if (this.horticola) {
            return getDropdownSelectedSingle(this.cultivos)?.cultivo ?? this.model.cultivo ?? '';
        }

        return this.getSelectedParcelas().map((it) => it.cultivo).join(';');
    }

    private resolveVariedadValue(): string {
        if (this.horticola) {
            return getDropdownSelectedSingle(this.variedades)?.variedad ?? this.model.variedad ?? '';
        }

        return this.getSelectedParcelas().map((it) => it.variedad).join(';');
    }

    private getSelectedParcelas(): ParcelasModel[] {
        const selected = getDropdownSelected(this.parcelas);

        if (Array.isArray(selected)) {
            return selected.filter((it): it is ParcelasModel => Boolean(it));
        }

        return selected ? [selected] : [];
    }


    private resolveClientId(): string {
        const modelClient = this.model.id_cliente;

        if (this.client?.id) {
            return String(this.client.id);
        }

        const selectedFinca = getDropdownSelectedSingle(this.fincas);

        if (selectedFinca?.id_cliente) {
            return String(selectedFinca.id_cliente);
        }

        if (typeof modelClient === 'object' && modelClient !== null) {
            return modelClient.id ?? '';
        }

        return String(modelClient ?? '');
    }

    private getDefaultFechaFin(): string | Date | undefined {
        if (!environment.features.horaFechaFinActualPorDefecto || this.formType === FormType.EDITION) {
            return undefined;
        }

        if (this.appName === 'sirga') {
            return undefined;
        }

        return new Date();
    }

    private getDefaultHoraFin(): string | undefined {
        if (!environment.features.horaFechaFinActualPorDefecto || this.formType === FormType.EDITION) {
            return undefined;
        }

        const now = new Date();

        return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    }

    private formatHoursMinutes(startTime: string, endTime: string): string {
        if (!startTime || !endTime) {
            return '';
        }

        const start = moment(startTime, 'HH:mm', true);
        const end = moment(endTime, 'HH:mm', true);

        if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
            return '';
        }

        const duration = moment.duration(end.diff(start));
        const hours = Math.floor(duration.asHours()).toString().padStart(2, '0');
        const minutes = duration.minutes().toString().padStart(2, '0');

        return `${hours}:${minutes}`;
    }

    private normalizeTimeValue(value: unknown): string {
        if (value instanceof Date) {
            return moment(value).format('HH:mm');
        }

        if (typeof value === 'string') {
            return value.trim();
        }

        return '';
    }

    private textInputType(): InputType {
        return this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.TEXT;
    }

    private dateInputType(): InputType {
        return this.isReadonlyMode() ? InputType.ONLY_TEXT : InputType.CALENDAR;
    }

    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }

    private pushFields(fields: FormField<TareaSiembraModel>[]): FormField<TareaSiembraModel>[] {
        const visibleFields = fields.filter((field) => field.conditions.isVisible && field.input.type !== InputType.HIDDEN);

        this.fieldNames = this.fieldNames.concat(visibleFields);
        this.fieldLabels = this.fieldLabels.concat(visibleFields.map((field) => field.label));

        return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private isReadonlyMode(): boolean {
        return this.formType >= FormType.DELETION && this.formType !== FormType.GENERATION;
    }

    private getFieldByName(name: string): FormField<TareaSiembraModel> | null {
        for (const section of this.formFields.sections) {
            const found = section.fields.find((field) => field.name === name);

            if (found) {
                return found;
            }
        }

        return null;
    }

    private ensureModelDefaults(): void {
        if (this.appName === 'agrosalvi' && typeof this.model.facturado === 'string') {
            this.model.facturado = this.model.facturado === '1';
        }

        if (this.appName === 'viverospereira' && typeof this.model.no_esta === 'string') {
            this.model.no_esta = this.model.no_esta === '1';
        }

        if (typeof this.model.check_previa === 'string') {
            this.model.check_previa = this.model.check_previa === '1' || this.model.check_previa === 'true';
        }
    }
}
